// First-party frontend analytics. Fire-and-forget event logging into Supabase
// (client_events via the log_client_event RPC). Works for anonymous users too,
// so we can watch the whole auth funnel — including drop-offs we can't
// reproduce locally (e.g. iOS Safari bouncing off /auth/callback).
//
// track() never throws and never blocks the UI: failures are swallowed.

import { getSupabase } from "./supabase.ts";
import { IS_TEST } from "./env.ts";

const SESSION_KEY = "ts_analytics_sid";
// Bumped per deploy via build define; "dev" locally. Correlates events to a build.
const APP_VERSION = (process.env.APP_VERSION as string) || "dev";

function sessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

/** Log a client event. Fire-and-forget — resolves even if logging fails. */
export function track(event: string, props: Record<string, unknown> = {}): void {
  // Don't pollute analytics from the test/CI harness.
  if (IS_TEST) return;
  const sb = getSupabase();
  if (!sb) return;
  try {
    // The Postgrest builder is a thenable; two-arg then swallows both outcomes.
    void sb
      .rpc("log_client_event", {
        p_event: event,
        p_path: typeof location !== "undefined" ? location.pathname : null,
        p_props: props,
        p_session_id: sessionId(),
        p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        p_app_version: APP_VERSION,
      })
      .then(
        () => {},
        () => {},
      );
  } catch {
    /* never let analytics break the app */
  }
}

/** Coarse client fingerprint for grouping (e.g. iOS-Safari-only bugs). */
export function clientHints(): Record<string, unknown> {
  if (typeof navigator === "undefined") return {};
  const ua = navigator.userAgent;
  return {
    ua_ios: /iPhone|iPad|iPod/.test(ua),
    ua_safari: /Safari/.test(ua) && !/CriOS|FxiOS|Chrome/.test(ua),
    ua_version: (ua.match(/Version\/(\d+\.\d+)/) || [])[1] ?? null,
    standalone: (navigator as unknown as { standalone?: boolean }).standalone ?? null,
    cookies_enabled: navigator.cookieEnabled,
  };
}
